import { useEffect, useMemo } from "react";

import { getCameraImage } from "@/utils/cameraImage";
import { logUnknownCameraModel } from "@/services/unknownCameraService";
import styles from "./CameraImage.module.css";

export type CameraImageVariant = "thumbnail" | "hero" | "inline" | "badge";

interface CameraImageProps {
  modelName: string | null | undefined;
  manufacturer?: string | null;
  variant?: CameraImageVariant;
  className?: string;
}

function buildFallbackLabel(
  modelName: string | null | undefined,
  manufacturer: string | null | undefined,
): string {
  if (modelName && manufacturer) return `${manufacturer} ${modelName}`;
  if (modelName) return modelName;
  if (manufacturer) return manufacturer;
  return "Camera";
}

export function CameraImage({
  modelName,
  manufacturer,
  variant = "thumbnail",
  className,
}: CameraImageProps) {
  const lookup = useMemo(
    () => getCameraImage(modelName, manufacturer),
    [modelName, manufacturer],
  );

  useEffect(() => {
    if (modelName && !lookup.hasImage) {
      void logUnknownCameraModel(modelName, manufacturer);
    }
  }, [modelName, manufacturer, lookup.hasImage]);

  const variantClass = styles[variant];
  const rootClass = [styles.root, variantClass, className].filter(Boolean).join(" ");

  if (!lookup.hasImage || !lookup.url) {
    const fallbackClass = [styles.root, styles.fallback, variantClass, className]
      .filter(Boolean)
      .join(" ");
    return (
      <span
        className={fallbackClass}
        aria-label={buildFallbackLabel(modelName, manufacturer)}
        title={buildFallbackLabel(modelName, manufacturer)}
      >
        {variant === "inline" || variant === "badge" ? "📷" : buildFallbackLabel(modelName, manufacturer)}
      </span>
    );
  }

  return (
    <span className={rootClass}>
      <img
        src={lookup.url}
        alt={buildFallbackLabel(modelName, manufacturer)}
        loading="lazy"
        decoding="async"
      />
    </span>
  );
}
